import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  PermanentConfirmStrategy, useConfirmationModal,
} from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { useDeleteEntityAccess } from '~/_shared/entityAccess/entityAccess.hooks';
import {
  type DeleteEntityAccessErrorResponse, EntityAccessErrorCode,
} from '~/_shared/entityAccess/entityAccess.repository';
import type { ApiError } from '~/_shared/utils/api/apiError.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { AppErrorType } from '~/appError/appErrorType.enum';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';

export const REMOVE_USER_FROM_MAP_NOT_SHOW_AGAIN_KEY = 'DO_NOT_ASK_FOR_REMOVE_MAP_SHARE_CONFIRMATION';

export const useRemoveMemberFromMap = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const clientId = useClientIdSelector();
  const { openConfirmationModal, closeConfirmationModal, replaceCurrentModalWithConfirmationModal } = useConfirmationModal();

  const {
    delete: deleteEntityAccess,
    isLoading: isDeletingEntityAccess,
  } = useDeleteEntityAccess();

  const removeUserFromMapRequest = useCallback(({
    isCurrentUserSharee,
    entityAccessId,
    onSuccess,
  }: {
    isCurrentUserSharee: boolean;
    entityAccessId: number;
    onSuccess?: () => void;
  }) => {
    if (!clientId) {
      return;
    }

    const onConfirm = (overrideMapUsedAsBaseMap?: boolean) => {
      deleteEntityAccess({
        clientId,
        entityAccessId,
        overrideMapUsedAsBaseMap,
      })
        .then(() => {
          closeConfirmationModal();
          onSuccess?.();
        })
        .catch((e: ApiError) => {
          if ((e.response as DeleteEntityAccessErrorResponse)?.code === EntityAccessErrorCode.MAP_USED_AS_BASE_MAP) {
            replaceCurrentModalWithConfirmationModal({
              cancelCaption: t('Cancel'),
              isConfirmButtonDestructive: true,
              confirmCaption: t('Proceed'),
              onCancel: closeConfirmationModal,
              title: t('Remove Member From Map'),
              text: isCurrentUserSharee
                ? t('teamShareMap.error.mapUsedAsBaseMap.myself')
                : t('teamShareMap.error.mapUsedAsBaseMap.otherMember'),
              onConfirm: () => {
                onConfirm(true);
              },
            });
            return;
          }

          dispatch(
            createAppError({
              type: AppErrorType.General,
              title: t('Error'),
              content: t('teamShareMap.editError'),
              errorTitle: t('teamShareMap.editErrorTitle'),
            })
          );
        });
    };

    openConfirmationModal({
      cancelCaption: t('Cancel'),
      isConfirmButtonDestructive: true,
      confirmCaption: t('Proceed'),
      onCancel: closeConfirmationModal,
      title: t('Remove Member From Map'),
      text: isCurrentUserSharee
        ? t('teamShareMap.removeMapAccessWarning.myself')
        : t('teamShareMap.removeMapAccessWarning.otherMember'),
      onConfirm,
      permanentConfirmSettings: {
        id: REMOVE_USER_FROM_MAP_NOT_SHOW_AGAIN_KEY,
        strategy: PermanentConfirmStrategy.Local,
      },
    });
  }, [clientId, closeConfirmationModal, deleteEntityAccess, dispatch, openConfirmationModal, replaceCurrentModalWithConfirmationModal, t]);

  return {
    isLoading: isDeletingEntityAccess,
    removeUserFromMapRequest,
  };
};
